import { AccountRepository, CreateAccountData } from '../../domain/ports/AccountRepository';
import { Account } from '../../domain/models/Account';
import { AccountActionLink } from '../../domain/models/AccountStatus';

/**
 * AccountService - Application Service
 * Orchestrates account-related use cases
 * Provides a simplified API for the presentation layer
 */
export class AccountService {
  constructor(private accountRepository: AccountRepository) {}

  /**
   * Get all accounts
   * @returns Promise<Account[]> List of all accounts
   * @throws Error if the operation fails
   */
  async getAllAccounts(): Promise<Account[]> {
    try {
      return await this.accountRepository.getAll();
    } catch (error) {
      // Application layer can add additional error handling/logging here
      console.error('AccountService: Failed to get all accounts', error);
      throw error;
    }
  }

  /**
   * Execute an account action using backend-provided action link
   * @param actionLink HATEOAS action link from backend
   * @returns Promise<Account | null> Updated account after action or null if no content
   * @throws Error if the operation fails
   */
  async executeAccountAction(actionLink: AccountActionLink): Promise<Account | null> {
    try {
      return await this.accountRepository.executeAccountAction(actionLink);
    } catch (error) {
      console.error('AccountService: Failed to execute account action', error);
      throw error;
    }
  }

  /**
   * Get a single account by ID
   * @param id Account UUID
   * @returns Promise<Account> The account
   * @throws Error if the operation fails
   */
  async getAccountById(id: string): Promise<Account> {
    try {
      return await this.accountRepository.getById(id);
    } catch (error) {
      console.error('AccountService: Failed to get account by id', error);
      throw error;
    }
  }

  /**
   * Update basic data (name and timezone) for an existing account
   * @param id Account UUID
   * @param data Object containing name and timezone
   * @throws Error if the operation fails
   */
  async updateAccount(id: string, data: { name: string; timezone: string }): Promise<void> {
    try {
      await this.accountRepository.update(id, data);
    } catch (error) {
      console.error('AccountService: Failed to update account', error);
      throw error;
    }
  }

  /**
   * Permanently delete an account (hard delete)
   * Account must be in REMOVED status
   * @param id Account UUID
   * @throws Error if the operation fails
   */
  async deleteAccount(id: string): Promise<void> {
    try {
      await this.accountRepository.delete(id);
    } catch (error) {
      console.error('AccountService: Failed to delete account', error);
      throw error;
    }
  }

  /**
   * Manually register a new business account.
   * @param data Account registration payload (RegisterAccountRequest shape)
   * @returns Object with the new tenantId minted by the backend (202 response)
   * @throws Error if the operation fails
   */
  async createAccount(data: CreateAccountData): Promise<{ tenantId: string }> {
    try {
      return await this.accountRepository.create(data);
    } catch (error) {
      console.error('AccountService: Failed to create account', error);
      throw error;
    }
  }
}
